// CompanionTalkTab.tsx
import React, { useState } from "react";
import { MessageCircle } from "lucide-react";
import AstriaMexicoShell from "../AstriaMexicoShell";
import { useMexicoColors } from "../AstriaMexicoThemeContext";
import {
  AstriaMexicoResultCard,
  MXLoadingState,
  MXErrorState,
  MXChatCta,
} from "../AstriaMexicoCard";
import { MXTextInput, MXSubmitButton } from "../AstriaMexicoWizard";
import { useAstriaMexicoTab } from "../useAstriaMexicoTab";
import { AstriaMexicoCompanionTalkData, Message } from "../../../../contexts/ChatContext";

const SUBCATEGORY_NAME = "Companion Talk";

// Helper function to extract JSON from the message content
const extractJSONFromMessage = (content: string): any => {
  try {
    // Look for the pattern <<<ASTRIA_MEXICO_DATA>>> ... <<<END_ASTRIA_MEXICO_DATA>>>
    const match = content.match(
      /<<<ASTRIA_MEXICO_DATA>>>\s*([\s\S]*?)\s*<<<END_ASTRIA_MEXICO_DATA>>>/,
    );
    if (match && match[1]) {
      return JSON.parse(match[1].trim());
    }
    return null;
  } catch (error) {
    console.error("Failed to parse JSON from message:", error);
    return null;
  }
};

// Colors must be read INSIDE AstriaMexicoShell's subtree — these small
// components are rendered as `children`, not evaluated inline, so their hook
// calls resolve after the shell's theme provider has mounted.

const ResultView: React.FC<{
  data?: AstriaMexicoCompanionTalkData;
  message: Message | null;
  reset: () => void;
  goToChat: () => Promise<void>;
}> = ({ data, message, reset, goToChat }) => {
  const { colors } = useMexicoColors();

  // This lane is a chat engine, not a structured reading — a single flowing
  // reply, no field labels or section breaks.
  if (data) {
    const reply = [data.reflection, data.observation, data.question]
      .filter((part) => part && part.trim())
      .join(" ");

    return (
      <>
        <AstriaMexicoResultCard
          icon={<MessageCircle className="w-5 h-5" />}
          title="Companion Talk"
          shareText={reply}
        >
          <p style={{ fontSize: 15, lineHeight: 1.75, color: colors.bodyColor }}>{reply}</p>
        </AstriaMexicoResultCard>
        <button
          onClick={reset}
          className="text-xs font-semibold transition-colors"
          style={{ marginTop: 14, color: colors.mutedColor }}
        >
          Empezar Una Nueva Conversación
        </button>
        <MXChatCta onClick={goToChat} />
      </>
    );
  }

  if (message) {
    // Check if the message contains the ASTRIA marker
    if (message.content.includes("<<<ASTRIA_MEXICO_DATA>>>")) {
      const parsedData = extractJSONFromMessage(message.content);
      if (parsedData) {
        // If we successfully parsed the data, render the structured view
        return (
          <ResultView
            data={parsedData as AstriaMexicoCompanionTalkData}
            message={message}
            reset={reset}
            goToChat={goToChat}
          />
        );
      }
    }

    // If no structured data, show the raw message
    return (
      <AstriaMexicoResultCard icon={<MessageCircle className="w-5 h-5" />} title="Companion Talk">
        <p
          style={{
            fontSize: 15,
            lineHeight: 1.75,
            color: colors.bodyColor,
            whiteSpace: "pre-wrap",
          }}
        >
          {message.content}
        </p>
      </AstriaMexicoResultCard>
    );
  }

  return null;
};

const IntroForm: React.FC<{
  feeling: string;
  setFeeling: (v: string) => void;
  onSubmit: () => void;
}> = ({ feeling, setFeeling, onSubmit }) => {
  const { colors } = useMexicoColors();

  return (
    <>
      <div style={{ marginBottom: 20 }}>
        <h2
          style={{
            fontSize: 21,
            fontWeight: 700,
            color: colors.titleColor,
            marginBottom: 6,
          }}
        >
          Companion Talk
        </h2>
        <p style={{ fontSize: 13.5, color: colors.mutedColor, lineHeight: 1.6 }}>
          Parece que traes algo en mente. Cuéntame un poco, si quieres — sin prisa.
        </p>
      </div>

      <div style={{ marginBottom: 20 }}>
        <MXTextInput
          value={feeling}
          onChange={setFeeling}
          placeholder="¿Qué has estado pensando últimamente? (opcional)"
        />
      </div>

      <MXSubmitButton onClick={onSubmit} label="Empezar la Conversación" />
    </>
  );
};

const CompanionTalkTab: React.FC = () => {
  const { loading, error, message, send, reset, goToChat } = useAstriaMexicoTab({
    subCategoryName: SUBCATEGORY_NAME,
  });

  const [feeling, setFeeling] = useState("");
  const started = !!message || loading || !!error;

  const handleSubmit = () =>
    send(
      `Quiero platicar en Companion Talk. Esto es lo que traigo en mente: ${feeling.trim() || "No estoy muy seguro, solo quería platicarlo."}`,
    );

  // Try to get structured data from the message
  const data = (() => {
    if (message?.astriaMexicoData) {
      return message.astriaMexicoData as AstriaMexicoCompanionTalkData | undefined;
    }
    // If no astriaMexicoData, try to extract from content
    if (
      message?.content &&
      message.content.includes("<<<ASTRIA_MEXICO_DATA>>>")
    ) {
      const parsedData = extractJSONFromMessage(message.content);
      if (parsedData) {
        return parsedData as AstriaMexicoCompanionTalkData;
      }
    }
    return undefined;
  })();

  if (started) {
    return (
      <AstriaMexicoShell active="companion-talk">
        {loading && <MXLoadingState label="Escuchando…" />}
        {!loading && error && <MXErrorState message={error} onRetry={handleSubmit} />}
        {!loading && !error && (
          <ResultView data={data} message={message} reset={reset} goToChat={goToChat} />
        )}
      </AstriaMexicoShell>
    );
  }

  return (
    <AstriaMexicoShell active="companion-talk">
      <IntroForm feeling={feeling} setFeeling={setFeeling} onSubmit={handleSubmit} />
    </AstriaMexicoShell>
  );
};

export default CompanionTalkTab;
